import { useDocumentVisibility } from '@vueuse/core';
import { onUnmounted, watch } from 'vue';

import { SpotMarketStatus } from '@/model/carrierPortal/SpotMarketStatus';
import {
  type InviteCarriersResult,
  type RateShopOwner,
  useSpotMarketStore,
} from '@/stores/SpotMarket';

const SPOT_MARKET_POLL_INTERVAL = 5000;

const SPOT_MARKET_POLL_DURATION_MAX = 5 * 60 * 1000;

export const useSpotMarketPolling = () => {
  const store = useSpotMarketStore();
  const visibility = useDocumentVisibility();

  const spotMarketPollingIntervals: Record<
    number,
    ReturnType<typeof setTimeout> | undefined
  > = {};
  const spotMarketMaxDurationTimeouts: Record<
    number,
    ReturnType<typeof setTimeout> | undefined
  > = {};
  const rateShopOwners: Record<number, RateShopOwner | undefined> = {};
  // Per-rateShopId generation token. Without this, a poll that fires, awaits,
  // and then sees the chain restarted (e.g. by a visibility-change refresh or
  // a user re-invite) would leak its `setTimeout` handle alongside the new one
  // and double the API rate.
  const pollGenerations: Record<number, number> = {};

  const stopSpotMarketPolling = (rateShopId: number): void => {
    clearTimeout(spotMarketPollingIntervals[rateShopId]);
    spotMarketPollingIntervals[rateShopId] = undefined;
    clearTimeout(spotMarketMaxDurationTimeouts[rateShopId]);
    spotMarketMaxDurationTimeouts[rateShopId] = undefined;
    rateShopOwners[rateShopId] = undefined;
    pollGenerations[rateShopId] = (pollGenerations[rateShopId] ?? 0) + 1;
  };

  const pollSpotMarkets = async (rateShopId: number): Promise<void> => {
    const owner = rateShopOwners[rateShopId];
    if (!owner) {
      return;
    }

    await store.refreshSpotMarkets(rateShopId, owner);

    const spotMarketResults = store.spotMarkets.value[rateShopId]?.data ?? [];

    if (spotMarketResults.length === 0) {
      stopSpotMarketPolling(rateShopId);
      return;
    }

    if (
      spotMarketResults.every((sm) => sm.status !== SpotMarketStatus.SENDING)
    ) {
      stopSpotMarketPolling(rateShopId);
    }
  };

  const resetMaxDurationTimeout = (rateShopId: number): void => {
    clearTimeout(spotMarketMaxDurationTimeouts[rateShopId]);
    spotMarketMaxDurationTimeouts[rateShopId] = setTimeout(() => {
      console.warn(
        `Spot market polling duration exceeded, rateShopId=${rateShopId}`,
      );
      stopSpotMarketPolling(rateShopId);
    }, SPOT_MARKET_POLL_DURATION_MAX);
  };

  const scheduleNextPoll = (rateShopId: number, gen: number): void => {
    if (pollGenerations[rateShopId] !== gen) {
      return;
    }
    spotMarketPollingIntervals[rateShopId] = setTimeout(() => {
      pollSpotMarkets(rateShopId).finally(() =>
        scheduleNextPoll(rateShopId, gen),
      );
    }, SPOT_MARKET_POLL_INTERVAL);
  };

  const startSpotMarketPolling = (
    rateShopId: number,
    owner: RateShopOwner,
  ): void => {
    stopSpotMarketPolling(rateShopId);

    rateShopOwners[rateShopId] = owner;
    scheduleNextPoll(rateShopId, pollGenerations[rateShopId]);
    resetMaxDurationTimeout(rateShopId);
  };

  const startSpotMarketPollingForResult = (
    result: InviteCarriersResult,
    owner: RateShopOwner,
  ): void => {
    const rateShopIds = new Set<number>();
    for (const sm of result.validSpotMarkets) {
      if (sm.rateShopId != null) {
        rateShopIds.add(sm.rateShopId);
      }
    }
    for (const rateShopId of rateShopIds) {
      startSpotMarketPolling(rateShopId, owner);
    }
  };

  const refreshActivePolls = (): void => {
    Object.keys(spotMarketPollingIntervals)
      .map((id) => +id)
      .forEach((rateShopId) => {
        if (!spotMarketPollingIntervals[rateShopId]) {
          return;
        }
        // Bump the generation so any in-flight poll's `.finally` from the old
        // chain bails instead of scheduling a parallel timer.
        pollGenerations[rateShopId] = (pollGenerations[rateShopId] ?? 0) + 1;
        const gen = pollGenerations[rateShopId];
        clearTimeout(spotMarketPollingIntervals[rateShopId]);
        spotMarketPollingIntervals[rateShopId] = undefined;
        resetMaxDurationTimeout(rateShopId);
        pollSpotMarkets(rateShopId).finally(() =>
          scheduleNextPoll(rateShopId, gen),
        );
      });
  };

  watch(visibility, (state) => {
    if (state === 'visible') {
      refreshActivePolls();
    }
  });

  onUnmounted(() => {
    Object.keys(spotMarketPollingIntervals)
      .map((id) => +id)
      .forEach((rateShopId) => stopSpotMarketPolling(rateShopId));
  });

  return {
    startSpotMarketPolling,
    startSpotMarketPollingForResult,
    stopSpotMarketPolling,
  };
};
